import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { fetchCguContent } from '@/store/actions/creators/cguContentActions';
import {
  cguContentSelector,
  cguContentFetchStatusSelector,
} from '@/store/selectors/cguContentSelector';
import { STARTED, SUCCEEDED } from '@/utils/helpers/store-helpers';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export default function Cgu() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const data = useSelector(cguContentSelector) as any;
  const fetchStatus = useSelector(cguContentFetchStatusSelector);

  useEffect(() => {
    // @ts-expect-error legacy redux-thunk
    dispatch(fetchCguContent());
  }, []);

  const html =
    i18n.language === 'fr'
      ? data?.contentFr || data?.contentEn || ''
      : data?.contentEn || data?.contentFr || '';

  const documentTitle =
    (i18n.language === 'fr'
      ? data?.titleFr || data?.titleEn
      : data?.titleEn || data?.titleFr) ||
    t('REGISTRATION.TERMS_AND_CONDITIONS') ||
    'Terms and conditions';

  const isLoading = fetchStatus === STARTED || (!fetchStatus && !data);

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/40 via-background to-muted/40 px-4 py-10 md:px-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold sm:text-3xl">{documentTitle}</h1>
          <Button asChild variant="outline" size="sm">
            <Link to="/login">{t('LOGIN.SIGN_IN') ?? 'Sign in'}</Link>
          </Button>
        </div>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">{documentTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : fetchStatus === SUCCEEDED && html ? (
              <div
                className="text-sm leading-relaxed [&_a]:text-primary [&_a]:underline [&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-primary/40 [&_blockquote]:pl-3 [&_blockquote]:italic [&_h1]:mb-2 [&_h1]:mt-5 [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:text-base [&_h3]:font-semibold [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('CGU.EMPTY') ?? 'No CGU content has been published yet.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
